// L'écran de séance de l'app (thème sombre), reproduit à ses dimensions
// réelles (390 points de large) puis réduit dans le téléphone. Disposition,
// tailles et couleurs reprises de screens/training/Seance.js, Exercice.js,
// components/Timer.js et SmallItemPicker.js.
//
// Animé une fois à l'apparition : la série 3 se remplit (reps, kilos, RPE) et
// passe en progression. Le minuteur reste au repos.
import React, { useEffect, useState } from "react";
import Apercu from "./Apercu";
import useApparition from "../useApparition";

// Étapes : 0 vide, 1 reps, 2 kilos, 3 RPE (série validée).
// Comme dans l'app, une case vide (0) est rouge : moins bien que la dernière fois.
const CALENDRIER = [800, 1500, 2200];
const ETAPE_VALIDEE = 3;

const mouvementReduit = () =>
	typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Couleur du RPE (Exercice.js, getRpeColor).
const couleurRpe = (v) => (v <= 7 ? "#FF9800" : v <= 8.5 ? "#F44336" : "#B71C1C");
// Pastilles de la ligne « EXERCICES » (Seance.js).
const couleurProgression = (s) => (s >= 50 ? "#4ade80" : s >= 35 ? "#facc15" : s >= 20 ? "#F87B1B" : "#ef4444");

const I = {
	retour: <path d="M19 12H5M11 6l-6 6 6 6" />,
	calendrier: (
		<>
			<rect x="4" y="5" width="16" height="15" rx="2" />
			<path d="M8 3v4M16 3v4M4 10h16" />
		</>
	),
	balance: (
		<>
			<rect x="4" y="4" width="16" height="16" rx="3" />
			<path d="M9 9a4 4 0 0 1 6 0M12 9.5l1.2-1.6" />
		</>
	),
	notes: <path d="M7 4h9a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7zM7 4v16M10 9h5M10 13h4" />,
	nuage: <path d="M7 18a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 18 9.6 4.2 4.2 0 0 1 17.5 18zM9.5 13.5l2 2 3.5-3.5" />,
	crayon: <path d="M4 20h4L19 9l-4-4L4 16zM14 6l4 4" />,
	roue: (
		<>
			<circle cx="12" cy="12" r="3" />
			<path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1" />
		</>
	),
	info: (
		<>
			<circle cx="12" cy="12" r="9" />
			<path d="M12 11v5M12 8h.01" />
		</>
	),
	corbeille: <path d="M5 7h14M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />,
	chevron: <path d="M6 15l6-6 6 6" />,
	feu: <path d="M12 3c1 3 4 5 4 9a4 4 0 0 1-8 0c0-2 1-3 1-3 .5 1.5 1.5 2 1.5 2C10 8 12 6 12 3z" />,
	partage: (
		<>
			<circle cx="17" cy="5" r="2.5" />
			<circle cx="6" cy="12" r="2.5" />
			<circle cx="17" cy="19" r="2.5" />
			<path d="M8.3 10.8l6.4-3.6M8.3 13.2l6.4 3.6" />
		</>
	),
	echange: <path d="M8 4v15M4 8l4-4 4 4M16 20V5M12 16l4 4 4-4" />,
	fleche: <path d="M12 19V5M6 11l6-6 6 6" />,
	hausse: <path d="M7 17L17 7M9 7h8v8" />,
	baisse: <path d="M7 7l10 10M17 9v8H9" />,
	plus: <path d="M12 5v14M5 12h14" />,
};

const Ic = ({ n, t = 16, c = "currentColor", e = 2 }) => (
	<svg width={t} height={t} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={e} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		{I[n]}
	</svg>
);

// Case de saisie (SmallItemPicker compact) : teal quand mieux que la dernière
// fois, rouge quand moins bien (une case vide l'est), neutre sinon.
function Case({ texte, etat = "neutre", saisie = false }) {
	return (
		<span className={`gs-app-case gs-app-case--${etat}${saisie ? " gs-app-case--saisie" : ""}`}>
			{texte}
		</span>
	);
}

function Rpe({ valeur, saisie = false }) {
	const c = valeur ? couleurRpe(Number(String(valeur).replace(",", "."))) : null;
	return (
		<span className={`gs-app-rpe${saisie ? " gs-app-case--saisie" : ""}`} style={c ? { color: c, borderColor: c, background: `${c}18` } : undefined}>
			<b>RPE</b>
			<i>{valeur || ""}</i>
		</span>
	);
}

function Serie({ numero, barre, cases, rpe, rpeSaisie = false, derniere, progression, objectif, derniereSerie }) {
	return (
		<div className="gs-app-serie">
			<div className="gs-app-serie-num">
				<span>S</span>
				<b>{numero}</b>
			</div>
			<div className="gs-app-serie-carte">
				<span className={`gs-app-barre gs-app-barre--${barre}`} />
				<div className="gs-app-serie-corps">
					<div className="gs-app-serie-ligne">
						<span className="gs-app-col-reps">{cases[0]}</span>
						<span className="gs-app-fois">×</span>
						<span className="gs-app-col-poids">
							<span className="gs-app-col-ent">{cases[1]}</span>
							<span className="gs-app-point">.</span>
							<span className="gs-app-col-dec">{cases[2]}</span>
							<span className="gs-app-unite">kg</span>
						</span>
						<span className="gs-app-col-rpe">
							<Rpe valeur={rpe} saisie={rpeSaisie} />
						</span>
					</div>
					<div className="gs-app-filet" />
					<div className="gs-app-info">
						<span className="gs-app-derniere">
							Dernière : {derniere}
							{progression && (
								<span className="gs-app-progression">
									Progression <Ic n="fleche" t={11} e={2.6} />
								</span>
							)}
						</span>
						{objectif && (
							<span className="gs-app-objectif">
								<span>Gym'Coach · </span>Objectif : {objectif}
							</span>
						)}
					</div>
				</div>
				<div className="gs-app-actions">
					<span className="gs-app-rond gs-app-rond--teal">
						<Ic n="partage" t={14} />
					</span>
					<span className="gs-app-rond gs-app-rond--rouge">
						<Ic n="corbeille" t={14} />
					</span>
					{!derniereSerie && (
						<span className="gs-app-rond gs-app-rond--teal">
							<Ic n="echange" t={14} />
						</span>
					)}
				</div>
			</div>
		</div>
	);
}

export default function ApercuSeance({ className = "" }) {
	const [refEcran, visible] = useApparition(0.5);
	const [reduit] = useState(mouvementReduit);
	const [etape, setEtape] = useState(() => (mouvementReduit() ? ETAPE_VALIDEE : 0));

	useEffect(() => {
		if (!visible || reduit) return undefined;
		const minuteries = CALENDRIER.map((t, i) => setTimeout(() => setEtape(i + 1), t));
		return () => minuteries.forEach(clearTimeout);
	}, [visible, reduit]);

	const validee = etape >= ETAPE_VALIDEE;
	const pctProgression = validee ? 67 : 50;

	return (
		<Apercu
			className={className}
			description="Écran de séance de l'app : séance « Pull », minuteur de repos, tirage horizontal en trois séries. La troisième se remplit, 7 × 80 kg à RPE 9, et passe en progression par rapport à 6 × 80 kg."
		>
			<div className="gs-tel gs-tel--app">
				<div className="gs-tel-ecran gs-tel-ecran--app" ref={refEcran}>
					<span className="gs-tel-encoche" />
					<div className="gs-app">
						{/* Ligne 1 : retour, puces date / poids / notes, synchro */}
						<div className="gs-app-ligne1">
							<span className="gs-app-retour">
								<Ic n="retour" t={20} c="#30a49a" />
							</span>
							<span className="gs-app-puces">
								<span className="gs-app-puce">
									<Ic n="calendrier" t={14} c="#30a49a" />
									07/10/26
								</span>
								<span className="gs-app-puce">
									<Ic n="balance" t={14} c="#30a49a" />
									73.4<small>KG</small>
								</span>
							</span>
							<span className="gs-app-puce">
								<Ic n="notes" t={15} c="#30a49a" />
								NOTES
							</span>
							<span className="gs-app-synchro">
								<Ic n="nuage" t={20} c="#4ade80" e={1.8} />
							</span>
						</div>

						{/* Nom de la séance, trait, minuteur */}
						<div className="gs-app-titre">
							PULL
							<Ic n="crayon" t={14} c="#30a49acc" />
						</div>
						<div className="gs-app-trait" />
						<div className="gs-app-minuteur">
							<span className="gs-app-jauge" />
							<span className="gs-app-minuteur-ligne">
								<span className="gs-app-roue">
									<Ic n="roue" t={22} c="#30a49a" />
								</span>
								<b>01:30</b>
								<span className="gs-app-lecture">
									<svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
										<path d="M8 5.5v13l11-6.5z" fill="#fff" />
									</svg>
								</span>
							</span>
						</div>

						{/* EXERCICES — filet — progression / régression — compteur */}
						<div className="gs-app-exercices">
							<span className="gs-app-eyebrow">EXERCICES</span>
							<span className="gs-app-regle" />
							<span className="gs-app-pastille" style={{ "--c": couleurProgression(pctProgression) }}>
								<Ic n="hausse" t={11} e={2.6} />
								{pctProgression}%
							</span>
							<span className="gs-app-pastille" style={{ "--c": "#4ade80" }}>
								<Ic n="baisse" t={11} e={2.6} />
								0%
							</span>
							<span className="gs-app-compteur">01</span>
						</div>

						<div className="gs-app-liste">
							{/* Carte d'exercice, dépliée */}
							<div className="gs-app-exo">
								<span className="gs-app-exo-accent" />
								<span className="gs-app-exo-nom">Tirage horizontal</span>
								<span className="gs-app-exo-actions">
									<Ic n="info" t={20} c="#30a49a" />
									<span className="gs-app-rond gs-app-rond--rouge gs-app-rond--28">
										<Ic n="corbeille" t={18} />
									</span>
									<Ic n="chevron" t={22} c="rgba(255,255,255,.6)" />
								</span>
							</div>
							<div className="gs-app-echauffement">
								<Ic n="feu" t={11} c="rgba(255,255,255,.33)" />
								Ajouter un échauffement
							</div>

							<Serie
								numero={1}
								barre="faite"
								cases={[<Case key="r" texte="8" />, <Case key="k" texte="82" etat="mieux" />, <Case key="d" texte="50" etat="mieux" />]}
								rpe="8"
								derniere="8 × 80 kg · RPE 8"
								progression
							/>
							<Serie
								numero={2}
								barre="faite"
								cases={[<Case key="r" texte="8" />, <Case key="k" texte="80" />, <Case key="d" texte="00" />]}
								rpe="8,5"
								derniere="8 × 80 kg · RPE 8,5"
							/>
							<Serie
								numero={3}
								derniereSerie
								barre={validee ? "faite" : "en-cours"}
								cases={[
									<Case key="r" texte={etape >= 1 ? "7" : "0"} etat={etape >= 1 ? "mieux" : "moins"} saisie={etape === 0} />,
									<Case key="k" texte={etape >= 2 ? "80" : "0"} etat={etape >= 2 ? "neutre" : "moins"} saisie={etape === 1} />,
									<Case key="d" texte="00" etat={etape >= 2 ? "neutre" : "moins"} />,
								]}
								rpe={validee ? "9" : ""}
								rpeSaisie={etape === 2}
								derniere="6 × 80 kg · RPE 9"
								progression={validee}
								objectif={validee ? null : "7 × 80 kg"}
							/>

							<div className="gs-app-ajout">
								<Ic n="plus" t={20} c="#30a49a" e={2.6} />
								Série
							</div>
						</div>
					</div>
				</div>
			</div>
		</Apercu>
	);
}
